<template>
  <v-app class="">
    <breakpointWatcher />
    <sig-dialog></sig-dialog>
    <plain-dialog></plain-dialog>
    <!-- <v-overlay v-model="sig.show" z-index="2"></v-overlay> -->
    <!-- Layout component -->
    <component :is="currentLayout" v-if="isRouterLoaded">
      <transition name="fade" mode="out-in">
        <router-view />
      </transition>
    </component>

    <v-snackbar v-model="toast.show" :timeout="toast.timeout" :color="toast.color" top>
      {{ toast.message }}
      <v-btn v-if="toast.timeout === 0" color="white" text @click="toast.show = false">{{ $t('common.close') }}</v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
  import { mapState } from 'vuex';
  import sigDialog from './components/shared/SigDialog.vue';
  import plainDialog from './components/shared/PlainDialog.vue';
  import config from './configs';
  import breakpointWatcher from './components/BreakpointWatcher.vue';

  // Layouts
  import defaultLayout from './layouts/DefaultLayout';
  import onePageLayout from './layouts/OnePageLayout';
  import authLayout from './layouts/AuthLayout';
  import errorLayout from './layouts/ErrorLayout';

  /*
|---------------------------------------------------------------------
| Main Application Component
|---------------------------------------------------------------------
|
| In charge of choosing the layout according to the router metadata
|
*/
  export default {
    components: {
      defaultLayout,
      onePageLayout,
      authLayout,
      errorLayout,
      sigDialog,
      plainDialog,
      breakpointWatcher
    },
    computed: {
      ...mapState('app', ['toast', 'sig', 'plainDialog']),
      isRouterLoaded: function () {
        if (this.$route.name !== null) return true;

        return false;
      },
      currentLayout: function () {
        const layout = this.$route.meta.layout || 'default';

        return layout + 'Layout';
      }
    },
    head: {
      link: [
        // adds config/icons into the html head tag
        ...config.icons.map((href) => ({ rel: 'stylesheet', href }))
      ]
    }
  };
</script>

<style>
  @font-face {
    font-family: 'Neue Plak';
    src: url('~@/assets/fonts/NeuePlak-Regular.ttf') format('truetype');
  }
  @font-face {
    font-family: 'Inter';
    src: url('~@/assets/fonts/Inter-Regular.ttf') format('truetype');
  }
  @font-face {
    font-family: 'Inter V';
    src: url('~@/assets/fonts/Inter-V.ttf') format('truetype');
  }
  /* Animations */
  .sigHelpLogo {
    background: transparent;
    /* box-shadow: 0 0 0 0 rgba(53, 1, 115, 1); */
    animation: pulse-sigLogo 1s;
  }
  @keyframes pulse-sigLogo {
    0% {
      transform: scale(0.2);
      /* box-shadow: 0 0 0 0 rgba(53, 1, 115, 0.7); */
    }

    90% {
      transform: scale(1.1);
      /* box-shadow: 0 0 0 10px rgba(53, 1, 115, 0); */
    }

    100% {
      transform: scale(1);
      /* box-shadow: 0 0 0 0 rgba(53, 1, 115, 0); */
    }
  }
  /* End Animations */
  /* Sigflo Font Styles */
  .sigfloXl {
    font-family: 'Neue Plak';
    font-weight: 400 !important;
    font-size: 84px !important;
    line-height: 85px !important;
  }
  .sigfloBody400 {
    font-family: 'Inter';
    font-weight: 400 !important;
    font-size: 18px !important;
    line-height: 22px !important;
  }
  .sigfloBody600 {
    font-family: 'Inter';
    font-weight: 600 !important;
    font-size: 18px !important;
    line-height: 22px !important;
  }
  .sigfloH5 {
    font-family: 'Inter';
    font-weight: 600 !important;
    font-size: 24px !important;
    line-height: 29px !important;
  }
  .sigfloH4 {
    font-family: 'Inter';
    font-weight: 600 !important;
    font-size: 32px !important;
    line-height: 39px !important;
  }
  .sigfloH3 {
    font-family: 'Inter';
    font-weight: 300 !important;
    font-size: 36px !important;
    line-height: 42px !important;
  }
  .sigfloH1 {
    font-family: 'Inter';
    font-weight: 600 !important;
    font-size: 48px !important;
    line-height: 58px !important;
  }
  .sigfloBodyLarge {
    font-family: 'Inter';
    font-weight: 600 !important;
    font-size: 22px !important;
    line-height: 27px !important;
  }
  .sigfloBodyMedium {
    font-family: 'Inter';
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    text-align: left;
  }
  .sigfloBodySubline1 {
    font-family: 'Inter';
    font-weight: 600 !important;
    font-size: 20px !important;
    line-height: 24px !important;
  }

  .sigfloBodySmall {
    font-family: 'Inter';
    font-weight: 400 !important;
    font-size: 14px !important;
    line-height: 17px !important;
  }
  .sigfloBodySmall600 {
    font-family: 'Inter';
    font-weight: 600 !important;
    font-size: 14px !important;
    line-height: 17px !important;
  }
  .sigfloButton {
    font-family: 'Inter' !important;
    font-weight: 600 !important;
    font-size: 18px !important;
    line-height: 22px !important;
  }
  .sigfloButtonSmall300 {
    font-family: 'Inter' !important;
    font-weight: 300 !important;
    font-size: 12px !important;
    line-height: 15px !important;
  }
  .sigfloButtonSmall400 {
    font-family: 'Inter' !important;
    font-weight: 400 !important;
    font-size: 12px !important;
    line-height: 15px !important;
  }
  .sigfloButtonSmall600 {
    font-family: 'Inter' !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    line-height: 15px !important;
  }
  /* End Sigflo Font Styles */
  /* Sigflo Scrollbar */

  /* For Chrome, Edge, and Safari */
  .sigfloScrollbar::-webkit-scrollbar {
    width: 22px; /* Width of the scrollbar */
  }

  .sigfloScrollbar::-webkit-scrollbar-track {
    background: #e9e5ee; /* Color of the track */
    border-radius: 10px; /* Fully rounded ends */
  }

  .sigfloScrollbar::-webkit-scrollbar-thumb {
    background-color: #c4a9ef; /* Color of the scrollbar itself */
    border-radius: 10px; /* Rounded corners on the scrollbar */
    border: 4px solid #e9e5ee; /* Optional: adds some spacing between track and thumb */
  }

  .sigfloScrollbar::-webkit-scrollbar-button {
    display: none; /* This removes the arrows */
  }
  /* End Sigflo Scrollbar */

  /* Colors */
  .sigfloMenuCard {
    background-color: #f9f6fd !important;
    border-radius: 16px !important;
  }
  .sigfloCard {
    border-radius: 20px !important;
  }
  .sigfloNeutralBorder200 {
    border: 1px solid #d2cbdd !important;
  }
  .sigfloNeutralBorder150 {
    border: 1px solid #ddd8e6 !important;
  }
  .brandPurple {
    color: #a67ee7 !important;
  }
  .brandGreen500 {
    color: #32ff98 !important;
  }
  .lightPurpleText {
    color: #c4a9ef !important;
  }
  .blueText {
    color: #344ffe !important;
  }
  .textEmphasis {
    color: #110b19 !important;
  }
  .textEmphasisLight {
    color: #fafafb !important;
  }
  .textLinkMuted {
    color: #bcb2cd !important;
  }
  .textMuted {
    color: #625676 !important;
  }
  .textMutedLight {
    color: #bcb2cd !important;
  }
  .textDefault {
    color: #352d41 !important;
  }
  .textLink {
    color: #7c40dc !important;
  }
  .elementMuted {
    background-color: #e9e5ee !important;
  }
  .mutedWhiteBackground {
    background-color: #f4f2f7;
  }
  /* End Colors */
  /* Overrides */
  .v-tooltip__content {
    background-color: #555 !important;
    color: #ffffff !important;
  }
  .v-btn--icon.v-size--default .v-icon,
  .v-btn--fab.v-size--default .v-icon {
    height: 18px !important;
    font-size: 18px !important;
    width: 18px !important;
  }
  .v-btn--icon.v-size--default {
    height: 28px !important;
    width: 28px !important;
  }
  .theme--light.v-btn.v-btn--icon {
    color: white;
  }
  .v-window__prev,
  .v-window__next {
    background-color: #7c40dc !important;
  }
  .v-menu__content {
    /* box-shadow: none; */
  }
  .v-text-field--outlined,
  .v-text-field--solo {
    border-radius: 8px !important;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
    border-bottom-left-radius: 8px;
  }
  .theme--light.v-text-field--filled > .v-input__control > .v-input__slot {
    background-color: #f4f2f7 !important;
  }
  .v-dialog {
    border-radius: 20px !important;
  }
  .v-overlay__scrim {
    background-color: rgba(196, 169, 239, 0.84) !important;
  }
  .theme--light.v-application {
    background-color: #200c40 !important;
    /* background-image: url('~@/assets/images/noise.png');
     background-repeat: repeat; */
    opacity: 1;
  }

  .text-size-sm {
    font-size: 12px; /* Set your desired font size for small screens */
  }

  .text-size-md {
    font-size: 16px; /* Set your desired font size for medium and larger screens */
  }
  .cardHover:hover {
    background-color: #e2e2e2 !important;
  }
  .theme--light.v-text-field--filled > .v-input__control > .v-input__slot {
    background-color: rgba(190, 190, 190, 0.1);
  }
  /**
  * Transition animation between pages
  */
  .fade-enter-active,
  .fade-leave-active {
    transition-duration: 0.2s;
    transition-property: opacity;
    transition-timing-function: ease;
  }

  .fade-enter,
  .fade-leave-active {
    opacity: 0;
  }
  .v-progress-circular__overlay {
    stroke-linecap: round;
    z-index: 2;
  }
  .v-progress-circular__underlay {
    stroke: rgba(158, 158, 158, 0.1);
    z-index: 1;
  }
  .progressCircle {
    position: absolute;
    top: 35px;
    left: 0px;
  }
  .progressCircleAvatar {
    position: absolute;
    top: 0px;
    left: 25px;
  }
  .progressCircleText {
    color: #352d41 !important;
    font-size: 14px;
    z-index: 2;
  }
  .progressCircleTextSmall {
    font-size: 12px;
    z-index: 2;
  }
</style>
